import AsyncStorage from "@react-native-async-storage/async-storage";

const STORAGE_KEY = "fail_trigger_library_v1";

/** @typedef {{ label: string, count: number, lastUsedAt: string }} SavedTrigger */

/** Starter cards (reference-style categories). count 0 until used. */
export const DEFAULT_FAIL_TRIGGERS = [
  "Mood",
  "Lifestyle",
  "Media",
  "Crush",
  "Friends",
  "Other",
];

export const TRIGGER_CHIP_COLORS = {
  mood: "#CA8A04",
  lifestyle: "#EA580C",
  media: "#DC2626",
  crush: "#DB2777",
  friends: "#4F46E5",
  other: "#0284C7",
};

/**
 * @param {string} label
 */
export function normalizeTriggerLabel(label) {
  return String(label || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 80);
}

/**
 * @param {string} label
 */
export function triggerKey(label) {
  return normalizeTriggerLabel(label).toLowerCase();
}

/**
 * @param {string} key
 */
export function colorForTriggerKey(key) {
  return TRIGGER_CHIP_COLORS[key] || "#64748B";
}

/**
 * @param {SavedTrigger[]} triggers
 */
export function sortTriggers(triggers) {
  return [...triggers].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return (b.lastUsedAt || "").localeCompare(a.lastUsedAt || "");
  });
}

/**
 * @param {unknown} raw
 * @returns {SavedTrigger[]}
 */
function normalizeLibrary(raw) {
  if (!raw || typeof raw !== "object") return [];
  const list = Array.isArray(raw.triggers) ? raw.triggers : Array.isArray(raw) ? raw : [];
  /** @type {Map<string, SavedTrigger>} */
  const map = new Map();
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const label = normalizeTriggerLabel(item.label);
    if (!label) continue;
    const key = triggerKey(label);
    const count = Number.isFinite(item.count) ? Math.max(0, Math.floor(item.count)) : 0;
    const prev = map.get(key);
    map.set(key, {
      label: prev?.label || label,
      count: Math.max(prev?.count ?? 0, count),
      lastUsedAt: typeof item.lastUsedAt === "string" ? item.lastUsedAt : prev?.lastUsedAt || "",
    });
  }
  return sortTriggers(Array.from(map.values()));
}

function seedDefaultLibrary() {
  const now = new Date().toISOString();
  return DEFAULT_FAIL_TRIGGERS.map((label) => ({
    label,
    count: 0,
    lastUsedAt: now,
  }));
}

/** @returns {Promise<SavedTrigger[]>} */
export async function loadTriggerLibrary() {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const seeded = seedDefaultLibrary();
      await saveTriggerLibrary(seeded);
      return seeded;
    }
    const parsed = normalizeLibrary(JSON.parse(raw));
    if (parsed.length === 0) {
      const seeded = seedDefaultLibrary();
      await saveTriggerLibrary(seeded);
      return seeded;
    }
    return ensureDefaultsPresent(parsed);
  } catch {
    return seedDefaultLibrary();
  }
}

/**
 * Keep default category cards available even if user only uses custom triggers.
 * @param {SavedTrigger[]} triggers
 */
function ensureDefaultsPresent(triggers) {
  const map = new Map(triggers.map((t) => [triggerKey(t.label), t]));
  for (const label of DEFAULT_FAIL_TRIGGERS) {
    const key = triggerKey(label);
    if (!map.has(key)) {
      map.set(key, { label, count: 0, lastUsedAt: "" });
    }
  }
  return sortTriggers(Array.from(map.values()));
}

/** @param {SavedTrigger[]} triggers */
export async function saveTriggerLibrary(triggers) {
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify({ triggers: sortTriggers(triggers) }));
}

/**
 * Merge triggers seen in calendar relapse entries (one-time backfill on load).
 * @param {SavedTrigger[]} library
 * @param {Record<string, { relapse?: { trigger?: string } }>} markedDates
 * @returns {SavedTrigger[]}
 */
export function mergeTriggersFromHistory(library, markedDates) {
  /** @type {Map<string, { label: string, count: number }>} */
  const hist = new Map();
  for (const entry of Object.values(markedDates ?? {})) {
    const label = normalizeTriggerLabel(entry?.relapse?.trigger || "");
    if (!label) continue;
    const key = triggerKey(label);
    const row = hist.get(key) || { label, count: 0 };
    row.count += 1;
    hist.set(key, row);
  }

  const map = new Map(library.map((t) => [triggerKey(t.label), { ...t }]));

  for (const [key, row] of hist) {
    const prev = map.get(key);
    if (prev) {
      prev.count = Math.max(prev.count, row.count);
    } else {
      map.set(key, { label: row.label, count: row.count, lastUsedAt: "" });
    }
  }

  return ensureDefaultsPresent(sortTriggers(Array.from(map.values())));
}

/**
 * @param {SavedTrigger[]} library
 * @param {string} label
 * @returns {Promise<SavedTrigger[]>}
 */
export async function recordTriggerUsage(library, label) {
  const normalized = normalizeTriggerLabel(label);
  if (!normalized) return library;

  const key = triggerKey(normalized);
  const now = new Date().toISOString();
  const map = new Map(library.map((t) => [triggerKey(t.label), { ...t }]));
  const prev = map.get(key);

  map.set(key, {
    label: normalized,
    count: (prev?.count ?? 0) + 1,
    lastUsedAt: now,
  });

  const next = ensureDefaultsPresent(sortTriggers(Array.from(map.values())));
  await saveTriggerLibrary(next);
  return next;
}

/**
 * Top triggers for the fail modal (used first, then defaults with 0 uses).
 * @param {SavedTrigger[]} library
 * @param {number} [limit]
 */
export function getDisplayTriggers(library, limit = 12) {
  const sorted = sortTriggers(library);
  const used = sorted.filter((t) => t.count > 0);
  const unusedDefaults = sorted.filter((t) => t.count === 0 && DEFAULT_FAIL_TRIGGERS.includes(t.label));
  const rest = sorted.filter((t) => !used.includes(t) && !unusedDefaults.includes(t));
  return [...used, ...unusedDefaults, ...rest].slice(0, limit);
}
